import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface FormErrors {
  title?: string;
  primaryGenre?: string;
  wordCount?: string;
  synopsis?: string;
  firstPages?: string;
  general?: string;
  [key: string]: string | undefined;
}

export const EditManuscriptPage = () => {
  // Extract manuscript ID from URL
  const manuscriptId = window.location.pathname.split('/')[3];

  const [formData, setFormData] = useState({
    title: '',
    primaryGenre: '',
    subGenre: '',
    wordCount: '',
    synopsis: '',
    firstPages: '',
    compTitle1: '',
    compTitle2: '',
    compTitle3: '',
    contentWarnings: '',
    saveAsDraft: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchManuscript();
  }, [manuscriptId]);

  const fetchManuscript = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.detail(manuscriptId), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch manuscript');
        setLoading(false);
        return;
      }

      const manuscript = data.manuscript;

      // Populate form with manuscript data
      setFormData({
        title: manuscript.title || '',
        primaryGenre: formatGenreToFrontend(manuscript.primaryGenre) || '',
        subGenre: manuscript.subGenre || '',
        wordCount: manuscript.wordCount?.toString() || '',
        synopsis: manuscript.synopsis || '',
        firstPages: manuscript.firstPages || '',
        compTitle1: manuscript.compTitles?.[0] || '',
        compTitle2: manuscript.compTitles?.[1] || '',
        compTitle3: manuscript.compTitles?.[2] || '',
        contentWarnings: manuscript.contentWarnings?.join(', ') || '',
        saveAsDraft: manuscript.status === 'DRAFT',
      });

      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscript error:', err);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  // Helper to format backend genre to frontend format
  const formatGenreToFrontend = (genre: string) => {
    const genreMap: Record<string, string> = {
      'FANTASY': 'fantasy',
      'SCIENCE_FICTION': 'science-fiction',
      'MYSTERY': 'mystery',
      'THRILLER': 'thriller',
      'ROMANCE': 'romance',
      'LITERARY_FICTION': 'literary-fiction',
      'HISTORICAL_FICTION': 'historical-fiction',
      'HORROR': 'horror',
      'YOUNG_ADULT': 'young-adult',
      'MIDDLE_GRADE': 'middle-grade',
      'CONTEMPORARY': 'contemporary',
      'PARANORMAL': 'paranormal',
      'DYSTOPIAN': 'dystopian',
      'OTHER': 'other',
    };
    return genreMap[genre] || genre?.toLowerCase()?.replace(/_/g, '-');
  };

  const genres = [
    { value: 'literary-fiction', label: 'Literary Fiction' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'thriller', label: 'Thriller' },
    { value: 'romance', label: 'Romance' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'science-fiction', label: 'Science Fiction' },
    { value: 'historical-fiction', label: 'Historical Fiction' },
    { value: 'horror', label: 'Horror' },
    { value: 'young-adult', label: 'Young Adult' },
    { value: 'memoir', label: 'Memoir' },
    { value: 'other', label: 'Other' },
  ];

  const subGenres: Record<string, Array<{ value: string; label: string }>> = {
    'literary-fiction': [
      { value: 'contemporary', label: 'Contemporary' },
      { value: 'experimental', label: 'Experimental' },
      { value: 'southern-gothic', label: 'Southern Gothic' },
    ],
    'historical-fiction': [
      { value: 'contemporary', label: 'Contemporary' },
      { value: 'medieval', label: 'Medieval' },
      { value: 'wwii', label: 'World War II' },
    ],
    'mystery': [
      { value: 'cozy', label: 'Cozy Mystery' },
      { value: 'hardboiled', label: 'Hardboiled' },
      { value: 'procedural', label: 'Police Procedural' },
    ],
    'fantasy': [
      { value: 'epic', label: 'Epic Fantasy' },
      { value: 'urban', label: 'Urban Fantasy' },
      { value: 'dark', label: 'Dark Fantasy' },
    ],
    // Add more as needed
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.title) {
      newErrors.title = 'Title is required';
    }

    if (!formData.primaryGenre) {
      newErrors.primaryGenre = 'Primary genre is required';
    }

    if (!formData.wordCount) {
      newErrors.wordCount = 'Word count is required';
    } else if (isNaN(Number(formData.wordCount)) || Number(formData.wordCount) < 1) {
      newErrors.wordCount = 'Please enter a valid word count';
    }

    if (!formData.synopsis) {
      newErrors.synopsis = 'Synopsis is required';
    } else if (formData.synopsis.split(' ').length > 500) {
      newErrors.synopsis = 'Synopsis must be 500 words or less';
    }

    if (!formData.firstPages) {
      newErrors.firstPages = 'First pages are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const compTitles = [formData.compTitle1, formData.compTitle2, formData.compTitle3]
        .filter(title => title.trim() !== '');
      const contentWarnings = formData.contentWarnings
        ? formData.contentWarnings.split(',').map(w => w.trim()).filter(w => w !== '')
        : [];

      const manuscriptData = {
        title: formData.title,
        primaryGenre: formatGenreToBackend(formData.primaryGenre),
        wordCount: parseInt(formData.wordCount),
        synopsis: formData.synopsis,
        firstPages: formData.firstPages,
        compTitles,
        contentWarnings,
      };

      const response = await fetch(API_ENDPOINTS.manuscripts.update(manuscriptId), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(manuscriptData),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Failed to update manuscript');
        setIsSaving(false);
        return;
      }

      setIsSaving(false);
      window.alert('Manuscript updated successfully!');
      window.location.href = `/writer/manuscripts/${manuscriptId}`;
    } catch (err) {
      console.error('Update manuscript error:', err);
      alert('Network error. Please try again.');
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${formData.title}"? This action cannot be undone.`)) {
      try {
        const response = await fetch(API_ENDPOINTS.manuscripts.delete(manuscriptId), {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        const data = await response.json();

        if (!response.ok) {
          alert(data.error || 'Failed to delete manuscript');
          return;
        }

        window.alert('Manuscript deleted successfully.');
        window.location.href = '/writer/manuscripts';
      } catch (err) {
        console.error('Delete manuscript error:', err);
        alert('Network error. Please try again.');
      }
    }
  };

  // Helper to format frontend genre to backend format
  const formatGenreToBackend = (genre: string) => {
    const genreMap: Record<string, string> = {
      'fantasy': 'FANTASY',
      'science-fiction': 'SCIENCE_FICTION',
      'mystery': 'MYSTERY',
      'thriller': 'THRILLER',
      'romance': 'ROMANCE',
      'literary-fiction': 'LITERARY_FICTION',
      'historical-fiction': 'HISTORICAL_FICTION',
      'horror': 'HORROR',
      'young-adult': 'YOUNG_ADULT',
      'middle-grade': 'MIDDLE_GRADE',
      'contemporary': 'CONTEMPORARY',
      'paranormal': 'PARANORMAL',
      'dystopian': 'DYSTOPIAN',
      'other': 'OTHER',
    };
    return genreMap[genre] || genre?.toUpperCase()?.replace(/-/g, '_');
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to discard your changes?')) {
      window.location.href = `/writer/manuscripts/${manuscriptId}`;
    }
  };

  const currentSubGenres = formData.primaryGenre && subGenres[formData.primaryGenre]
    ? subGenres[formData.primaryGenre]
    : [];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Loading State */}
        {loading && (
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
            border: `1px solid ${theme.colors.border}`,
            textAlign: 'center',
          }}>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Loading manuscript...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
            border: `1px solid ${theme.colors.border}`,
            textAlign: 'center',
            color: theme.colors.error,
          }}>
            <p style={{
              fontSize: '18px',
              marginBottom: '20px',
            }}>
              {error}
            </p>
            <Button onClick={fetchManuscript}>
              Try Again
            </Button>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <>
            {/* Page Header */}
            <div style={{
              marginBottom: '40px',
            }}>
              <h1 style={{
                fontSize: '40px',
                fontWeight: '400',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Edit Manuscript
              </h1>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Update your manuscript details
              </p>
            </div>

            {/* Form */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
              border: `1px solid ${theme.colors.border}`,
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
            gap: '32px',
          }}>
            {/* Title */}
            <Input
              label="Title"
              type="text"
              placeholder="Enter your manuscript title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={errors.title}
              required
            />

            {/* Genre Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
            }}>
              <Select
                label="Primary Genre"
                value={formData.primaryGenre}
                onChange={(e) => setFormData({ ...formData, primaryGenre: e.target.value, subGenre: '' })}
                options={genres}
                placeholder="Select primary genre"
                error={errors.primaryGenre}
                required
              />

              <Select
                label="Sub-Genre"
                value={formData.subGenre}
                onChange={(e) => setFormData({ ...formData, subGenre: e.target.value })}
                options={currentSubGenres}
                placeholder={currentSubGenres.length > 0 ? 'Select sub-genre' : 'Select primary genre first'}
                helperText="Optional"
              />
            </div>

            {/* Word Count */}
            <Input
              label="Word Count"
              type="number"
              placeholder="e.g., 85000"
              value={formData.wordCount}
              onChange={(e) => setFormData({ ...formData, wordCount: e.target.value })}
              error={errors.wordCount}
              helperText="Approximate word count of your complete manuscript"
              required
            />

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Synopsis */}
            <Textarea
              label="Synopsis"
              placeholder="Provide a compelling synopsis of your manuscript..."
              value={formData.synopsis}
              onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
              maxLength={5000}
              rows={8}
              error={errors.synopsis}
              helperText="Maximum 500 words - This will be visible to beta readers"
              required
            />

            {/* First Pages */}
            <Textarea
              label="First Pages"
              placeholder="Paste the first 10-15 pages or first chapter..."
              value={formData.firstPages}
              onChange={(e) => setFormData({ ...formData, firstPages: e.target.value })}
              rows={12}
              error={errors.firstPages}
              helperText="This sample helps beta readers decide if they want to read your manuscript"
              required
            />

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Comp Titles */}
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Comparative Titles
              </h3>
              <p style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
                lineHeight: '1.6',
              }}>
                List 2-3 published books that are similar to yours in style, tone, or content
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                <Input
                  placeholder="e.g., Title by Author"
                  value={formData.compTitle1}
                  onChange={(e) => setFormData({ ...formData, compTitle1: e.target.value })}
                />
                <Input
                  placeholder="e.g., Title by Author"
                  value={formData.compTitle2}
                  onChange={(e) => setFormData({ ...formData, compTitle2: e.target.value })}
                />
                <Input
                  placeholder="e.g., Title by Author (Optional)"
                  value={formData.compTitle3}
                  onChange={(e) => setFormData({ ...formData, compTitle3: e.target.value })}
                />
              </div>
            </div>

            {/* Content Warnings */}
            <Textarea
              label="Content Warnings"
              placeholder="List any content warnings (violence, explicit content, sensitive topics, etc.)"
              value={formData.contentWarnings}
              onChange={(e) => setFormData({ ...formData, contentWarnings: e.target.value })}
              maxLength={500}
              rows={3}
              helperText="Optional but recommended - Helps beta readers make informed decisions"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <Button variant="ghost" onClick={handleDelete} style={{ color: theme.colors.error }}>
            Delete Manuscript
          </Button>
          <div style={{
            display: 'flex',
            gap: '12px',
          }}>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isSaving}>
              {isSaving ? 'Updating...' : 'Update Manuscript'}
            </Button>
          </div>
        </div>

        {/* Version History (Optional Feature) */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: theme.colors.backgroundAlt,
          borderRadius: theme.borderRadius.large,
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Version History
          </h3>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            marginBottom: '16px',
          }}>
            Track changes to your manuscript over time
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {[
              { date: '2 days ago', change: 'Updated synopsis and first pages' },
              { date: '1 week ago', change: 'Added comparative title' },
              { date: '2 weeks ago', change: 'Updated word count to 95,000' },
              { date: '3 weeks ago', change: 'Initial manuscript creation' },
            ].map((version, index) => (
              <div
                key={index}
                style={{
                  padding: '12px 16px',
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.medium,
                  border: `1px solid ${theme.colors.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{
                  fontSize: '15px',
                  color: theme.colors.textPrimary,
                }}>
                  {version.change}
                </span>
                <span style={{
                  fontSize: '13px',
                  color: theme.colors.textTertiary,
                  fontFamily: theme.fonts.monospace,
                }}>
                  {version.date}
                </span>
              </div>
            ))}
          </div>
        </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};
